/**
 * The close evidence pack register (FSD 2.2.8.10 "Close Evidence Pack";
 * 2.2.8.6 GET /finance/close/evidence-pack/{periodId}; 2.2.8.8 "archived
 * immutably and retained for the statutory period").
 *
 *  - `closeEvidencePacksList`     GET  /close-evidence-packs — the register.
 *  - `closeRunsBuildEvidencePack` POST /close-runs/:id/evidence-pack — rebuilds
 *    the pack and reports whether its manifest hash still equals the one
 *    recorded at certification (`hashMatchesCertification`), never silently
 *    overwriting it. This is the register's "verify" action.
 *  - The ZIP itself streams from GET /close-runs/:id/evidence-pack. Its
 *    generated twin `closeRunsEvidencePack` returns `Promise<void>` and throws
 *    the body away, so the bytes come through the authenticated `rawRequest`
 *    seam the cockpit already uses (`downloadEvidencePack` in close/api.ts) —
 *    one download implementation, not a second.
 */

import {
  closeEvidencePacksList,
  type CloseEvidencePackDTO,
  type CloseEvidencePackSummaryDTO,
  type CloseEvidencePacksQuery,
} from '@erpx/api'

import { api } from '@/api/client'
import { buildEvidencePack, downloadEvidencePack } from '@/modules/close/api'

export type { CloseEvidencePackDTO, CloseEvidencePackSummaryDTO }

export const EVIDENCE_PACK_PERMISSIONS = {
  /** List, download and rebuild — `close.evidence.read` on all three routes. */
  read: 'close.evidence.read',
  runRead: 'close.run.read',
} as const

export const EVIDENCE_PACK_KEYS = {
  all: ['close', 'evidence-packs'] as const,
  page: (params: Record<string, unknown>) => ['close', 'evidence-packs', 'list', params] as const,
} as const

export interface EvidencePackFilters {
  companyId: number | null
  periodId: number | null
  cursor: string
}

export interface EvidencePackPage {
  items: CloseEvidencePackSummaryDTO[]
  nextCursor: string | null
  hasMore: boolean
}

export function listEvidencePacks(filters: EvidencePackFilters, limit = 50): Promise<EvidencePackPage> {
  const query: CloseEvidencePacksQuery = {
    limit,
    ...(filters.cursor === '' ? {} : { cursor: filters.cursor }),
    ...(filters.companyId === null ? {} : { companyId: filters.companyId }),
    ...(filters.periodId === null ? {} : { periodId: filters.periodId }),
  }
  return closeEvidencePacksList(api(), query)
}

/**
 * The match state a rebuild reports:
 *  - `matches`: certified, and the rebuilt manifest hash equals the certified one;
 *  - `differs`: certified, and the inputs moved after the certifier signed;
 *  - `uncertified`: no hash was recorded at certification to compare with.
 */
export type PackMatchState = 'matches' | 'differs' | 'uncertified'

export function matchState(pack: CloseEvidencePackDTO): PackMatchState {
  if (pack.certifiedHash === undefined || pack.certifiedHash === '') return 'uncertified'
  return pack.hashMatchesCertification ? 'matches' : 'differs'
}

export function verifyEvidencePack(runId: number, idempotencyKey: string): Promise<CloseEvidencePackDTO> {
  return buildEvidencePack(runId, idempotencyKey)
}

export { downloadEvidencePack }
